/**
 * NOTA — `paragraph` en Typography
 *
 * MUI 9 removio ese prop. Los `{...({ paragraph: true } as object)}` de este archivo lo
 * siguen pasando, pero hoy se ignora: esos bloques perdieron su margin-bottom y ya no
 * renderizan como parrafo. Se conserva el comportamiento actual; reemplazarlo por
 * sx + component="p" cambia como se ve y va en su propio ticket.
 *
 * NOTA — `!= null` en vez de `!== null`
 *
 * VacancyDTO declara todos sus campos como opcionales porque springdoc no marca ninguno
 * como `required`. En runtime, en cambio, Jackson serializa los nulls (el DTO no lleva
 * @JsonInclude), asi que estos campos llegan como `null`, nunca ausentes. El `!= null`
 * cubre los dos casos y deja que TypeScript estreche el tipo; el comportamiento real
 * no cambia.
 */
import { useEffect, useState } from "react";

import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import { Box } from "@mui/material";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import type { SweetAlertIcon } from "sweetalert2";

import { MainLayout } from "@shared/ui/MainLayout";
import { useLoading } from "@shared/hooks/useLoading";
import MDBox from "@vendor/components/MDBox";

import { PostulateCandidateDialog } from "../components/PostulateCandidateDialog";
import { useVacancy } from "../hooks/useVacancy";

interface ErrorMessage {
  title: string;
  icon: SweetAlertIcon;
}

// `default` se declara aparte para que el fallback del lookup quede tipado como presente.
const ERROR_MESSAGES: Record<string, ErrorMessage> & { default: ErrorMessage } = {
  503: { title: "Servicio no disponible", icon: "error" },
  404: { title: "Recurso no encontrado", icon: "error" },
  default: { title: "Error interno", icon: "error" },
};

export const JobDetailPage = () => {
  const { t } = useTranslation();
  const { loading, notLoading, thisLoading } = useLoading();
  const [openDialog, setOpenDialog] = useState(false);
  const { id } = useParams();

  const { data: vacancy, isFetching, isError, error } = useVacancy(id);

  useEffect(() => {
    if (!isError) return;
    const errorResponse = error?.response;
    // String(undefined) da "undefined", que no es una clave del mapa, asi que sin status
    // sigue cayendo en `default` igual que antes.
    const { title, icon } =
      ERROR_MESSAGES[String(errorResponse?.status)] || ERROR_MESSAGES.default;
    Swal.fire(title, errorResponse?.data?.message || "Ocurrió un error inesperado", icon);
  }, [isError, error]);

  const handleClickOpen = () => {
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
  };

  return (
    <MainLayout loading={loading || isFetching}>
      <MDBox py={3}>
        {vacancy !== undefined && (
          <>
            <Card>
              <CardHeader title={vacancy.name} />
              <CardContent>
                <Typography variant="h5">{t("description")}:</Typography>
                {vacancy.description != null && vacancy.description.length > 0 ? (
                  <Box
                    dangerouslySetInnerHTML={{ __html: vacancy.description }}
                    sx={{
                      fontSize: "15px",
                      mb: 2.5,
                      lineHeight: 1.6,
                      color: "text.primary",
                      "& p": {
                        mb: 2,
                      },
                      "& ul, & ol": {
                        ml: 3,
                        mb: 2,
                      },
                      "& li": {
                        mb: 0.5,
                      },
                      "& h1, & h2, & h3, & h4": {
                        mt: 3,
                        mb: 2,
                        fontWeight: 600,
                        color: "text.primary",
                      },
                      "& a": {
                        color: "primary.main",
                        textDecoration: "none",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      },
                    }}
                  />
                ) : (
                  <Typography
                    {...({ paragraph: true } as object)}
                    style={{ fontSize: "15px", marginTop: 20 }}
                  >
                    -
                  </Typography>
                )}
                {vacancy.responsibilities != null && vacancy.responsibilities.length > 0 && (
                  <>
                    <Typography variant="h5">{t("responsibilities")}:</Typography>
                    <Typography {...({ paragraph: true } as object)} style={{ fontSize: "15px" }}>
                      {vacancy.responsibilities}
                    </Typography>
                  </>
                )}
                {vacancy.techStack != null && vacancy.techStack.length > 0 && (
                  <>
                    <Typography variant="h5">{t("requiredTechnologies")}:</Typography>
                    <Typography component={"div"} style={{ fontSize: "15px" }}>
                      <ul style={{ marginLeft: 30 }}>
                        {vacancy.techStack.map((techStack, index) => (
                          <li key={index}>{techStack}</li>
                        ))}
                      </ul>
                    </Typography>
                  </>
                )}
                {vacancy.language != null && vacancy.language.length > 0 && (
                  <>
                    <Typography variant="h5">{t("requiredLanguage")}:</Typography>
                    <Typography {...({ paragraph: true } as object)} style={{ fontSize: "15px" }}>
                      {vacancy.language}
                    </Typography>
                  </>
                )}
                {vacancy.estimatedHiringTime != null &&
                  vacancy.estimatedHiringTime.length > 0 && (
                    <>
                      <Typography variant="h5">{t("estimatedDurationTime")}:</Typography>
                      <Typography {...({ paragraph: true } as object)} style={{ fontSize: "15px" }}>
                        {vacancy.estimatedHiringTime}
                      </Typography>
                    </>
                  )}
              </CardContent>
              <MDBox mb={2} mr={2} ml={2} mt={2}>
                <Grid
                  container
                  spacing={2}
                  sx={{
                    flexDirection: "row",
                    justifyContent: "flex-end",
                    alignItems: "flex-end",
                  }}
                >
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                      lg: 3,
                    }}
                  >
                    <MDBox mb={2}>
                      <Button
                        onClick={handleClickOpen}
                        style={{ backgroundColor: "#479925", color: "white" }}
                        variant="contained"
                        fullWidth
                      >
                        {t("nominateCandidate")}
                      </Button>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>

            <PostulateCandidateDialog
              handleClose={handleClose}
              open={openDialog}
              vacancyId={vacancy.id}
              vacancyTitle={vacancy.name}
              notLoading={notLoading}
              loading={thisLoading}
              t={t}
            />
          </>
        )}
      </MDBox>
    </MainLayout>
  );
};
